/**
 * The app's own invitation: which card travels, in which shape, and what it is
 * called once it leaves the app (SPEC-020 D-7).
 *
 * The plant equivalent is `domain/plants/plantSheet.js`, and this follows it
 * deliberately — the address is a RULE stated here and restated for the build in
 * `scripts/share/shareCardPath.js`, bound by `appShare.test.js`. Two owners of
 * one generated path is how a share quietly degrades to a link with nothing in
 * the diff to explain it.
 *
 * Pure: no fetch, no `navigator`, no URL object. Loading the file is
 * `infrastructure/content/appShareCardFile.js`, and sharing it is
 * `application/pwa/shareApp.js`.
 */

/**
 * Two shapes, because a share sheet and a link preview want different pictures
 * and neither crops into the other without losing the composition (D-2).
 *
 * `unfurl` is 1.91:1 because that is what every unfurling platform crops to.
 * `portrait` is 4:5 because that is the tallest a story or a message bubble
 * renders without letterboxing, and it is the shape the phone is already held in.
 */
export const SHARE_CARD_SHAPES = {
    unfurl: { key: 'unfurl', width: 1200, height: 630 },
    portrait: { key: 'portrait', width: 1080, height: 1350 },
};

/**
 * The languages a card has actually been rendered in.
 *
 * The same three the message catalogues carry, and not a coincidence: the card's
 * whole vocabulary is `brand.proposition` and `home.intro`, so a language with a
 * catalogue is a language with a card.
 */
export const SHARE_CARD_LANGUAGES = ['de', 'en', 'hu'];

const FALLBACK_LANGUAGE = 'en';

/**
 * English for anything unrecognised, matching `getCatalogue()`, which already
 * answers the same way for the same input.
 *
 * There is no such language today. The fallback exists because the filename is
 * derived from a value that arrives from the URL, and a 404 here would cost the
 * picture on a share that had no other reason to fail.
 */
export function shareCardLanguage(languageCode) {
    return SHARE_CARD_LANGUAGES.includes(languageCode) ? languageCode : FALLBACK_LANGUAGE;
}

/** Same-origin, so the service worker can cache it and no CORS is involved. */
export function appShareCardPath(languageCode, shapeKey = 'portrait') {
    const shape = SHARE_CARD_SHAPES[shapeKey];

    if (!shape) {
        throw new Error(`No share card shape called ${shapeKey}`);
    }

    return `/assets/share/plant-habb-me/card-${shareCardLanguage(languageCode)}`
        + `-${shape.width}x${shape.height}.jpg`;
}

/**
 * What the card is called once it lands in someone's Photos roll.
 *
 * The product's name and nothing else — no language suffix, no shape, no build
 * id. A recipient reading `card-hu-1080x1350.jpg` learns nothing; `plant.habb.me`
 * is the one thing the file is FOR.
 *
 * The extension has to stay `.jpg`: several share targets decide how to treat an
 * attachment by looking at it.
 */
export function appShareCardFileName() {
    return 'plant.habb.me.jpg';
}
